/**
 * MP4/MOV demuxer using mp4box.js
 * Extracts audio track from MP4 container for WebCodecs processing
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import * as MP4BoxModule from 'mp4box';

// Get createFile from mp4box module
const MP4Box = (MP4BoxModule as any).default || MP4BoxModule;

import type { DemuxedAudioTrack, AudioSample } from '../types';

// MP4Box types (inline to avoid import issues with untyped library)
interface MP4ArrayBuffer extends ArrayBuffer {
  fileStart: number;
}

interface MP4AudioTrack {
  id: number;
  codec: string;
  nb_samples: number;
  duration: number;
  timescale: number;
  audio: {
    sample_rate: number;
    channel_count: number;
  };
}

interface Sample {
  data: ArrayBuffer;
  duration: number;
  cts: number;
  timescale: number;
  is_sync: boolean;
}

interface MP4Info {
  audioTracks: MP4AudioTrack[];
}

interface MP4Trak {
  mdia?: {
    minf?: {
      stbl?: {
        stsd?: {
          entries?: Array<{
            esds?: {
              esd?: {
                descs?: Array<{
                  tag: number;
                  data?: ArrayBuffer;
                }>;
              };
            };
            dOps?: unknown;
          }>;
        };
      };
    };
  };
}

interface MP4File {
  onReady?: (info: MP4Info) => void;
  onError?: (error: string) => void;
  onSamples?: (trackId: number, user: unknown, samples: Sample[]) => void;
  appendBuffer(buffer: MP4ArrayBuffer): number;
  start(): void;
  flush(): void;
  setExtractionOptions(
    trackId: number,
    user: unknown,
    options?: { nbSamples?: number }
  ): void;
  getTrackById(trackId: number): MP4Trak | undefined;
}

export interface DemuxerOptions {
  onProgress?: (progress: number) => void;
}

/**
 * Demux MP4/MOV file and extract audio track
 */
export async function demuxMP4Audio(
  file: File,
  options?: DemuxerOptions
): Promise<DemuxedAudioTrack> {
  return new Promise((resolve, reject) => {
    // Use 'any' to avoid type conflicts with mp4box's internal types
    const mp4boxFile = MP4Box.createFile() as unknown as MP4File;
    const samples: AudioSample[] = [];
    let audioTrack: MP4AudioTrack | null = null;
    let totalSamples = 0;
    let processedSamples = 0;

    mp4boxFile.onError = (error: string) => {
      reject(new Error(`MP4 demuxing failed: ${error}`));
    };

    mp4boxFile.onReady = (info) => {
      // Find the first audio track
      const tracks = info.audioTracks;
      if (!tracks || tracks.length === 0) {
        reject(new Error('No audio track found in video file'));
        return;
      }

      audioTrack = tracks[0];
      totalSamples = audioTrack.nb_samples;

      // Set extraction options
      mp4boxFile.setExtractionOptions(audioTrack.id, null, {
        nbSamples: 100, // Extract in batches
      });

      // Start extraction
      mp4boxFile.start();
    };

    mp4boxFile.onSamples = (
      _trackId: number,
      _user: unknown,
      extractedSamples: Sample[]
    ) => {
      for (const sample of extractedSamples) {
        samples.push({
          data: new Uint8Array(sample.data),
          timestamp: (sample.cts * 1_000_000) / sample.timescale, // Convert to microseconds
          duration: (sample.duration * 1_000_000) / sample.timescale,
          isKeyFrame: sample.is_sync,
        });
        processedSamples++;
      }

      // Report progress
      if (totalSamples > 0) {
        options?.onProgress?.(processedSamples / totalSamples);
      }

      // Check if all samples extracted
      if (processedSamples >= totalSamples && audioTrack) {
        // Build codec description
        const description = getCodecDescription(mp4boxFile, audioTrack);

        resolve({
          codec: audioTrack.codec,
          sampleRate: audioTrack.audio.sample_rate,
          channelCount: audioTrack.audio.channel_count,
          description,
          samples,
          duration: audioTrack.duration / audioTrack.timescale,
        });
      }
    };

    // Read file in chunks
    const reader = file.stream().getReader();
    let offset = 0;

    const readChunk = async () => {
      try {
        const { done, value } = await reader.read();
        if (done) {
          mp4boxFile.flush();
          return;
        }

        // Convert to MP4ArrayBuffer
        const buffer = value.buffer as MP4ArrayBuffer;
        buffer.fileStart = offset;
        offset += value.byteLength;

        mp4boxFile.appendBuffer(buffer);
        readChunk();
      } catch (error) {
        reject(error);
      }
    };

    readChunk();
  });
}

/**
 * Get codec-specific description (AudioSpecificConfig for AAC)
 */
function getCodecDescription(
  mp4boxFile: MP4File,
  track: MP4AudioTrack
): Uint8Array | undefined {
  const trak = mp4boxFile.getTrackById(track.id);
  if (!trak) return undefined;

  // Navigate to the codec-specific box
  // For AAC: moov > trak > mdia > minf > stbl > stsd > mp4a > esds
  // For other codecs, the structure may vary
  const stsd = trak.mdia?.minf?.stbl?.stsd;
  if (!stsd) return undefined;

  const entry = stsd.entries?.[0];
  if (!entry) return undefined;

  // Check for AAC (mp4a with esds)
  if ('esds' in entry && entry.esds?.esd?.descs) {
    const descs = entry.esds.esd.descs;
    for (const desc of descs) {
      if (desc.tag === 5 && desc.data) {
        // DecoderSpecificInfo tag
        return new Uint8Array(desc.data);
      }
    }
  }

  // Check for Opus (Opus box)
  if ('dOps' in entry && entry.dOps) {
    // For Opus, we may need to construct the description differently
    // For now, return undefined and let WebCodecs figure it out
    return undefined;
  }

  return undefined;
}

/**
 * Check if file is MP4/MOV format
 */
export function isMP4Format(file: File): boolean {
  const mp4Types = ['video/mp4', 'video/quicktime', 'video/x-m4v'];
  const ext = file.name.split('.').pop()?.toLowerCase();
  return mp4Types.includes(file.type) || ['mp4', 'mov', 'm4v'].includes(ext || '');
}
